import {
  Box,
  Chip,
  CircularProgress,
  Rating,
  Stack,
  Typography,
} from '@mui/material'
import { DriverDto, KycSubjectType, VehicleDto } from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { PropertyView, PropertySection, PropertyField } from './PropertyView'

function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime() - Date.now()
  return Math.floor(ms / 86400000)
}

function expiryChip(dateStr?: string) {
  const d = daysUntil(dateStr)
  if (d == null) return <Typography variant="caption" color="text.secondary">—</Typography>
  const label = new Date(dateStr as string).toLocaleDateString()
  if (d < 0) return <Chip label={`${label} (expired)`} color="error" size="small" />
  if (d <= 30) return <Chip label={`${label} (${d}d)`} color="warning" size="small" />
  return <Chip label={label} color="default" size="small" variant="outlined" />
}

function kycChipColor(s?: string): 'success' | 'warning' | 'error' | 'default' {
  if (s === 'VERIFIED') return 'success'
  if (s === 'REJECTED' || s === 'EXPIRED') return 'error'
  if (s === 'PENDING' || s === 'SUSPENDED') return 'warning'
  return 'default'
}

function formatVehicleDimensions(
  lengthM?: number,
  widthM?: number,
  heightM?: number
): string | null {
  if (lengthM == null && widthM == null && heightM == null) return null
  return [lengthM, widthM, heightM]
    .map((n) => (n != null ? `${n}m` : '?'))
    .join(' × ')
}

function capabilityChips(v: VehicleDto) {
  const tags: string[] = []
  if (v.boxTruck) tags.push('Box')
  if (v.curtainsider) tags.push('Curtainsider')
  if (v.flatbed) tags.push('Flatbed')
  if (v.hasHazmat) tags.push('Hazmat')
  if (v.hasRefrigeration) tags.push('Refrigerated')
  if (v.hasRamp) tags.push('Ramp')
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
      {tags.map((t) => (
        <Chip key={t} label={t} size="small" variant="outlined" />
      ))}
      {tags.length === 0 && (
        <Typography variant="caption" color="text.secondary">—</Typography>
      )}
    </Stack>
  )
}

export function getDriverDisplayName(driver?: DriverDto | null): string {
  if (!driver) return ''
  return driver.user?.name || driver.licenseNo || driver.id
}

export function getVehicleDisplayName(vehicle?: VehicleDto | null): string {
  if (!vehicle) return ''
  return vehicle.plateNo || vehicle.id
}

export function getSubjectDisplayName(
  subjectType: KycSubjectType | undefined,
  driver?: DriverDto | null,
  vehicle?: VehicleDto | null,
  subjectId?: string
): string {
  if (subjectType === 'DRIVER') {
    const name = getDriverDisplayName(driver)
    if (name) return name
  }
  if (subjectType === 'VEHICLE') {
    const name = getVehicleDisplayName(vehicle)
    if (name) return name
  }
  return subjectId ? `${subjectId.substring(0, 8)}…` : ''
}

interface SubjectDetailPanelProps {
  subjectType: KycSubjectType
  driver?: DriverDto | null
  vehicle?: VehicleDto | null
  isLoading?: boolean
}

export function SubjectStatusChips({
  subjectType,
  driver,
  vehicle,
}: {
  subjectType: KycSubjectType
  driver?: DriverDto | null
  vehicle?: VehicleDto | null
}) {
  const getLocalizedLabel = useLocalizedLabel()

  if (subjectType === 'DRIVER' && driver) {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 1 }}>
        <Chip
          label={
            getLocalizedLabel('kycVerificationStatus', driver.kycVerificationStatus || '') ||
            driver.kycVerificationStatus ||
            '—'
          }
          color={kycChipColor(driver.kycVerificationStatus)}
          size="small"
        />
        <Chip
          label={
            getLocalizedLabel('userStatus', driver.user?.status || '') ||
            driver.user?.status ||
            '—'
          }
          size="small"
          color={driver.user?.status === 'ACTIVE' ? 'success' : 'default'}
        />
        {driver.user?.rating != null && driver.user.rating > 0 && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Rating value={driver.user.rating} readOnly size="small" precision={0.5} />
            <Typography variant="caption" color="text.secondary">
              ({driver.user.ratingCount || 0})
            </Typography>
          </Stack>
        )}
      </Stack>
    )
  }

  if (subjectType === 'VEHICLE' && vehicle) {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 1 }}>
        <Chip
          label={
            getLocalizedLabel('kycVerificationStatus', vehicle.kycVerificationStatus || '') ||
            vehicle.kycVerificationStatus ||
            '—'
          }
          color={kycChipColor(vehicle.kycVerificationStatus)}
          size="small"
        />
        {vehicle.type && (
          <Chip
            label={getLocalizedLabel('vehicleType', vehicle.type) || vehicle.type}
            size="small"
            variant="outlined"
          />
        )}
        {vehicle.rating != null && vehicle.rating > 0 && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Rating value={Number(vehicle.rating)} readOnly size="small" precision={0.5} />
            <Typography variant="caption" color="text.secondary">
              ({vehicle.ratingCount || 0})
            </Typography>
          </Stack>
        )}
      </Stack>
    )
  }

  return null
}

export default function SubjectDetailPanel({
  subjectType,
  driver,
  vehicle,
  isLoading,
}: SubjectDetailPanelProps) {
  const getLocalizedLabel = useLocalizedLabel()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (subjectType === 'DRIVER' && driver) {
    return (
      <PropertyView>
        <PropertySection title="Identity">
          <PropertyField label="Driver ID" value={driver.id} mono copyable />
          <PropertyField label="User ID" value={driver.user?.id} mono copyable />
          <PropertyField
            label="Account ID"
            value={driver.accountId ?? driver.user?.accountId}
            mono
            copyable
            full
          />
        </PropertySection>
        <PropertySection title="Contact">
          <PropertyField label="Email" value={driver.user?.email} />
          <PropertyField label="Phone" value={driver.user?.phone} />
        </PropertySection>
        <PropertySection title="License">
          <PropertyField label="License No" value={driver.licenseNo} />
          <PropertyField label="License Expiry">
            {expiryChip(driver.licenseExpiry)}
          </PropertyField>
        </PropertySection>
        <PropertySection title="Activity">
          <PropertyField label="KYC Verified At" value={driver.kycVerifiedAt} type="datetime" />
          <PropertyField label="Last login" value={driver.user?.lastLoginAt} type="datetime" />
          <PropertyField label="Created" value={driver.createdAt} type="datetime" full />
        </PropertySection>
      </PropertyView>
    )
  }

  if (subjectType === 'VEHICLE' && vehicle) {
    return (
      <PropertyView>
        <PropertySection title="Identity">
          <PropertyField label="Vehicle ID" value={vehicle.id} mono copyable />
          <PropertyField label="Account ID" value={vehicle.accountId} mono copyable />
          {vehicle.defaultDriverId && (
            <PropertyField
              label="Default Driver ID"
              value={vehicle.defaultDriverId}
              mono
              copyable
              full
            />
          )}
        </PropertySection>
        <PropertySection title="Specs" columns={3}>
          <PropertyField label="Plate" value={vehicle.plateNo} />
          <PropertyField label="Maker" value={vehicle.maker} />
          <PropertyField label="Model" value={vehicle.model} />
          <PropertyField label="Year" value={vehicle.year} />
          <PropertyField
            label="Capacity"
            value={
              vehicle.capacityKg
                ? `${vehicle.capacityKg.toLocaleString()} kg`
                : null
            }
          />
          <PropertyField
            label="Dimensions (L × W × H)"
            value={formatVehicleDimensions(
              vehicle.lengthM,
              vehicle.widthM,
              vehicle.heightM
            )}
          />
          <PropertyField
            label="Fuel (L/100km)"
            value={vehicle.fuelConsumptionPer100Km}
          />
          <PropertyField
            label="Loading Side"
            value={
              vehicle.loadingSide
                ? getLocalizedLabel('loadingSide', vehicle.loadingSide) ||
                  vehicle.loadingSide
                : null
            }
          />
        </PropertySection>
        <PropertySection title="Compliance">
          <PropertyField label="License Expiry">
            {expiryChip(vehicle.licenseExpiry)}
          </PropertyField>
          <PropertyField label="Last Test" value={vehicle.lastTestDate} type="date" />
        </PropertySection>
        <PropertySection title="Operations">
          <PropertyField label="Capabilities" full>
            <Box>{capabilityChips(vehicle)}</Box>
          </PropertyField>
          <PropertyField
            label="Home Base"
            value={
              vehicle.homeBaseAddress ||
              (vehicle.homeBaseLatitude && vehicle.homeBaseLongitude
                ? `${vehicle.homeBaseLatitude}, ${vehicle.homeBaseLongitude}`
                : null)
            }
            full
          />
          {vehicle.vehicleImgs && vehicle.vehicleImgs.length > 0 && (
            <PropertyField
              label={`Vehicle pictures (${vehicle.vehicleImgs.length})`}
              value={`Stored as ${vehicle.vehicleImgs.length} file(s) — preview not implemented.`}
              full
            />
          )}
        </PropertySection>
      </PropertyView>
    )
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Subject details could not be loaded.
    </Typography>
  )
}
